import { Progress } from '@/components/ui/progress';
import { CONTENT_TYPES, IconMap } from '@/lib/constants';
import { ContentType } from '@/types/custom';

interface ContentPlays {
  types: {
    [key: string]: {
      plays: number;
    };
  };
}

interface TypesBarChartProps {
  contentPlays: ContentPlays;
}

/**
 * Bar graph of most played content types.
 */
export function TypesBarChart({ contentPlays }: TypesBarChartProps) {
  let items = Object.entries(contentPlays.types)
    .filter(([_, stats]) => stats?.plays && stats?.plays > 0) // Only show types with activity
    .map(([type, stats]) => ({
      icon: IconMap[type as ContentType],
      name: CONTENT_TYPES[type as ContentType],
      sessions: stats?.plays || 0,
      progress: 100,
    }))
    .sort((a, b) => b.sessions - a.sessions); // Sort by most played

  // ensure all contents types are present
  for (const type of Object.keys(CONTENT_TYPES)) {
    if (
      !items.find((item) => item.name === CONTENT_TYPES[type as ContentType])
    ) {
      items.push({
        icon: IconMap[type as ContentType],
        name: CONTENT_TYPES[type as ContentType],
        sessions: 0,
        progress: 0,
      });
    }
  }

  // Calculate total sessions played for weighting progress
  let totalSessions = items.reduce(
    (acc, item) => acc + (item.sessions || 0),
    0,
  );
  if (totalSessions === 0) {
    totalSessions = 1;
  }

  items = items.map((item) => ({
    ...item,
    progress: totalSessions > 0 ? (item.sessions / totalSessions) * 100 : 0,
  }));

  return (
    <div className="space-y-6">
      {items.map((item) => (
        <div key={item.name} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <item.icon className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm">{item.name}</p>
            </div>
            <span className="text-sm text-muted-foreground">
              {item.sessions} sessies
            </span>
          </div>
          <Progress
            value={item.progress}
            className="h-2 bg-secondary [&>div]:bg-[#00A67E]"
          />
        </div>
      ))}
    </div>
  );
}
